<template>
	<ViewFeedItem class="view-feed-youtube">
		<template v-slot:header>
			<ViewLayoutModuleHeader title="Found on YouTube">
				<!-- <RouterLink v-bind:to="{ name: 'PagePlaces' }">
					<strong>View All</strong>
				</RouterLink> -->
			</ViewLayoutModuleHeader>
		</template>

		<template v-slot:body>
			<section v-if="contentModel && contentModel.id">
				<ChalkyContentVideoPlayer v-bind:contentModel="contentModel" />
				<p class="type-small-paragraph" v-html="contentModel.getContent()"></p>
			</section>
		</template>

		<template v-slot:footer>
			<div class="attribution" v-if="youtubeUrl">
				<picture class="avatar size-sm">
					<img v-if="iconUrl" v-bind:src="iconUrl" crossorigin="anonymous" referrerpolicy="no-referrer" />
				</picture>

				<div class="channel-info" v-if="youtubeName">
					<strong>{{ youtubeName }}</strong>
					<p>on YouTube</p>
				</div>

				<div>
					<a class="btn button-secondary" target="_blank" v-bind:href="youtubeUrl">
						<span>View</span>
					</a>
				</div>
			</div>
		</template>
	</ViewFeedItem>
</template>

<script lang="ts">
	import ChalkySticks from '@chalkysticks/sdk';
	import ViewBase from '@/Core/View.vue';
	import { Component, Prop } from 'vue-property-decorator';
	import { beforeDestroy, created, mounted } from '@/Utility/Decorators';

	/**
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package View/Feed
	 * @project ChalkySticks App
	 */
	@Component
	export default class ViewFeedYouTube extends ViewBase {
		/**
		 * Directly assigning these with "=" caused stack overflow
		 * merge issues when returning back to a page with this on it.
		 *
		 * @return ChalkySticks.Collection.Content
		 */
		@Prop({
			default: () => ChalkySticks.Factory.Content.collection(),
		})
		public contentCollection!: ChalkySticks.Collection.Content;

		/**
		 * @type string[]
		 */
		@Prop({
			default: ['video'],
		})
		public tags!: string[];

		/**
		 * Directly assigning these with "=" caused stack overflow
		 * merge issues when returning back to a page with this on it.
		 *
		 * @return ChalkySticks.Model.Content
		 */
		@Prop({
			default: () => ChalkySticks.Factory.Content.model(),
		})
		protected contentModel!: ChalkySticks.Model.Content;

		/**
		 * @type string
		 */
		protected iconUrl!: string;

		/**
		 * @type string
		 */
		protected youtubeName: string = '...';

		/**
		 * @type string
		 */
		protected youtubeUrl: string = 'https://www.youtube.com';

		/**
		 * @return Promise<void>
		 */
		@created
		public async afterCreate(): Promise<void> {
			await this.contentCollection.tags(this.tags).fetch();

			// Get the first item
			this.contentModel = this.contentCollection.shuffle().first();

			// If we have a youtubeName, then we can set the iconUrl
			this.iconUrl = this.contentModel.getIconUrl();

			// If we have a YouTube URL, we can set the youtubeName
			this.fetchChannelData();
		}

		/**
		 * @return Promise<void>
		 */
		protected async fetchChannelData(): Promise<void> {
			const youtubeUrl = this.getYouTubeVideo();

			// If we don't have a YouTube URL, return the default icon URL
			if (!youtubeUrl) {
				return;
			}

			// mk: Swap with factory in new SDK
			const scheduleModel = ChalkySticks.TV.Factory.Channel.model();
			scheduleModel.setEndpoint('content');
			scheduleModel.setId(`${this.contentModel.id}/detail`);
			await scheduleModel.fetch();

			// Set details
			this.iconUrl = scheduleModel.getThumbnail() || this.iconUrl;
			this.youtubeName = scheduleModel.getTitle() || this.youtubeName;
			this.youtubeUrl = scheduleModel.getUrl() || this.youtubeUrl;
		}

		/**
		 * @return string
		 */
		protected getYouTubeVideo(): string {
			const mediaUrl = this.contentModel.getMediaUrl();
			const isYouTube = mediaUrl && !!mediaUrl.match(/youtube\.com|youtu\.be/i);

			// Not a YouTube video
			if (!isYouTube) {
				return '';
			}

			return mediaUrl;
		}

		// region: Event Handlers
		// ---------------------------------------------------------------------------

		/**
		 * @return Promise<void>
		 */
		protected async Handle_OnClickRefresh(): Promise<void> {
			this.contentModel = this.contentCollection.shuffle().first();
		}

		// endregion: Event Handlers
	}
</script>

<style lang="scss">
	.view-feed-youtube {
		.body {
			padding: 0 var(--padding);
		}

		.chalky.content-videoplayer {
			margin-bottom: 1rem;
		}

		.attribution {
			align-items: center;
			display: flex;
			gap: 1rem;
			padding: 2rem 1rem;

			.channel-info {
				flex: 1;
				line-height: 1;
			}
		}
	}
</style>
